import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CloudUpload, RefreshCw } from 'lucide-react';
import { outboxActions, useOnlineStatus, useOutbox } from '@sportschool/shared-logic';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { PendingSyncEntryRow } from './PendingSyncEntryRow';

/**
 * Global badge with the number of changes waiting to be sent (and rejected ones in red).
 * Tap opens the list of queued entries with "Send now" and per-entry Retry / Discard. Hidden when the outbox is empty.
 */
export function PendingSyncIndicator() {
  const { t } = useTranslation();
  const { entries, pendingCount, rejectedCount, flushing } = useOutbox();
  const { isOnline } = useOnlineStatus();
  const [open, setOpen] = useState(false);

  if (pendingCount + rejectedCount === 0) return null;

  const hasRejected = rejectedCount > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            // 44px touch target; -my-1 keeps its layout height at the topbar's 36px so it does not shift the page when it appears
            'h-11 -my-1 min-w-11 gap-1.5 rounded-xl px-2.5 text-xs font-bold cursor-pointer',
            hasRejected ? 'text-destructive hover:text-destructive' : 'text-warning'
          )}
          data-testid="pending-sync-indicator"
          aria-label={t('sync.indicatorLabel', { pending: pendingCount, rejected: rejectedCount })}
        >
          {flushing ? <Spinner className="size-4" /> : <CloudUpload className="size-4" aria-hidden="true" />}
          {pendingCount > 0 && <span data-testid="pending-sync-count">{pendingCount}</span>}
          {hasRejected && (
            <span className="inline-flex items-center gap-0.5 rounded-md bg-destructive/10 px-1.5 py-0.5 text-destructive">
              <AlertCircle className="size-3.5" aria-hidden="true" />
              {rejectedCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(92vw,22rem)] p-3">
        <div className="flex items-center justify-between gap-2 border-b pb-2">
          <div className="min-w-0">
            <p className="text-sm font-bold text-foreground">{t('sync.popoverTitle')}</p>
            <p className="text-[11px] text-muted-foreground">
              {isOnline ? t('sync.summary', { pending: pendingCount, rejected: rejectedCount }) : t('sync.waitingForConnection')}
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            className="h-11 shrink-0 gap-1.5 rounded-xl px-3 text-xs font-bold"
            disabled={flushing}
            onClick={() => void outboxActions.flush()}
          >
            <RefreshCw className={cn('size-3.5', flushing && 'animate-spin')} aria-hidden="true" />
            {t('sync.sendNow')}
          </Button>
        </div>
        <ul className="max-h-80 divide-y overflow-y-auto">
          {entries.map((entry) => (
            <PendingSyncEntryRow key={entry.key} entry={entry} />
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
